// ABOUTME: SPEC-86 D-15 — every tunable of the reel's swipe / scroll navigation, each with its reason,
// ABOUTME: plus the one flag rule (`reelSwipeNavProps`) App.js feeds to Projectionist. Data, not logic.

import { STAGE_FADE_MS } from '../../CollectionStage/useStageTransition';

// Gesture-initiated traversal only (owner ruling 3); agent moves keep the manifest rate. Owner
// 2026-10-13: 2.5 -> 3.5. ~26 Mbps sustained on the 7.5 Mbps master (reelSpecTransform's
// playbackRate note); a stall walks the stallGuard ladder 3.5 -> 1.4 -> 1.0x, inside its two rungs.
export const GESTURE_TRAVERSAL_RATE = 3.5;
// Phone only (owner 2026-10-13, from the 2026-10-12 staging phone sessions): at the then-live 2.5x the Xperia's gesture
// moves actually ran at 1-2.3x with 6-7 s first taps on the 1080x2400 / 7.3 Mbps portrait master - the stall ladder was
// already doing the pacing. 3.5x there is ~25 Mbps and ~105 decoded frames/s, which buys a stutter before the same
// slowdown, so the phone keeps 2.5 while desktop takes 3.5. Re-measure if a lighter phone encode (3-4 Mbps) ships.
export const GESTURE_TRAVERSAL_RATE_MOBILE = 2.5;
/** The gesture boost for a composition: useBreakpoint's `'mobile'` is the phone; anything else is desktop. */
export function gestureTraversalRate(composition) {
  return composition === 'mobile' ? GESTURE_TRAVERSAL_RATE_MOBILE : GESTURE_TRAVERSAL_RATE;
}
// Longer than any SPEC-58 round trip (a direct play_chapter, no model turn); short enough that a
// later agent move to the same view is not boosted by a stale intent.
export const GESTURE_INTENT_TTL_MS = 5000;
// Belt: longer than the longest authored move at the gesture rate (DIAL is 15.8 s at 1x).
export const GESTURE_ARM_MAX_MS = 20000;
// = useReelViewToggle's OPTIMISTIC_TIMEOUT_MS: a refused / debounced send never departs (SPEC-58).
export const LANDING_TIMEOUT_MS = 4000;
export const GESTURE_LOCK_MAX_MS = 20000;
// Owner report 2026-10-13 ("from Vélos I scroll up, nothing happens"): entering or leaving the Vélos stage never moves the film, so
// the lock above sat out the whole LANDING_TIMEOUT_MS. The stage swaps under the black, the lift takes one more fade, and a
// return's replayed play_chapter is released at the lift (SPEC-66) — so if it moves the film at all, it is seen moving in here.
export const STAGE_SETTLE_MS = 2 * STAGE_FADE_MS;
// Owner 2026-10-13, relaxing D-11's "dropped, not queued": the latest gesture made while the film moves is held and played once the
// move lands. Covers an agent-paced Crown move at 1x (~6.4 s + its exit); anything older is stale and is dropped.
export const HELD_GESTURE_MAX_AGE_MS = 8000;
export const HELD_GESTURE_POLL_MS = 100;
// The Views buttons learn "at rest" one render after the film does (onPlayerStateChange); a held gesture waits this long at rest.
export const HELD_GESTURE_SETTLE_MS = 150;
// Larger of a fixed floor or a viewport fraction, like the roll pager's 24 / 0.12, but a film
// swipe should be deliberate.
export const SWIPE_MIN_PX = 48;
export const SWIPE_MIN_FRACTION = 0.12;
export const SWIPE_DOMINANCE = 1.5; // a diagonal scroll attempt is not a step
export const SWIPE_MAX_MS = 800; // a slow drag or a hold is not a swipe
export const EDGE_EXCLUSION_PX = 24; // the iOS back-gesture band
// One mouse notch (Chrome 100 px, Firefox 3 lines x 16) fires; a trackpad needs a small deliberate swipe.
export const WHEEL_STEP_PX = 40;
export const WHEEL_LINE_PX = 16; // deltaMode 1 (lines) -> px
// Trackpad inertia arrives every ~16 ms until the train ends; a 250 ms gap is a new gesture.
export const WHEEL_QUIET_MS = 250;

// The `/api/config.ui_experiment` name (a dev-ophion overlay's UI_EXPERIMENT). Kept here, not in uiExperiment/toggles.js
// TOGGLE: it is not a SPEC-82 experiment component, and the experiment's test matrices iterate that list.
export const SWIPE_NAV_TOGGLE = 'swipe_nav';

const NO_BUTTONS = Object.freeze([]);

/**
 * D-13: swipe navigation is on only for a tenant that opts in AND already has the Views routing
 * (the backend answers `chapter_selected` only under `reel_view_toggle`, SPEC-58 B2) AND past the
 * intro. Absent flag = off; the frontend never defaults it on.
 * Rev 2 (owner 2026-10-09): the opt-in is EITHER the tenant profile flag `reel_swipe_nav` (the
 * permanent, every-environment switch) OR the `swipe_nav` UI-experiment toggle (`ux`, the env-scoped
 * switch an overlay's UI_EXPERIMENT carries — dev-ophion only while it is trialled).
 * SPEC-92 D-1: AND past the entry. `preEntry` = the no_enter_gate landing before the guest enters; a gesture there
 * would be held by the landing, enter the boutique and be flushed behind the welcome (the production "ghost" step).
 */
export function reelSwipeNavProps(featureFlags, buttons, showIntro, ux, preEntry = false) {
  const optedIn = featureFlags?.reel_swipe_nav === true || ux?.[SWIPE_NAV_TOGGLE] === true;
  const enabled = optedIn
    && featureFlags?.reel_view_toggle === true
    && !showIntro
    && !preEntry;
  return {
    enabled,
    chapterButtons: enabled && Array.isArray(buttons) ? buttons : NO_BUTTONS,
  };
}
